import { DateTime } from 'luxon';
import React, { FunctionComponent, useMemo } from 'react';
import ListDetails, { Field } from '../../../components/ListDetails';
import { ProfileBadge } from '../../../components/profile-badge';
import { Order, OrderType } from '../../../services/orders-service/types';

/**
 * OrderUser props
 */
interface Props {
  order: Order;
}

/**
 * OrderUser Component
 */
const OrderUser: FunctionComponent<Props> = ({ order }) => {
  const user = useMemo(() => order.user, [order.user]);
  const personalInfoFields = useMemo<Field[]>(
    () => [
      { key: 'firstName' },
      { key: 'middleName' },
      { key: 'lastName' },
      { key: 'birthDate' },
      { key: 'gender' },
      { key: 'phone' },
    ],
    [],
  );

  const orderInfoFields = useMemo<Field[]>(
    () =>
      [
        { key: 'shopifyId', label: 'Order No.' },
        { key: 'orderType', label: 'Order Type' },
        {
          key: 'orderDate',
          label: 'Order Date & Time (EST)',
          render: (data: Order) =>
            DateTime.fromJSDate(new Date(data.orderDate))
              .setZone('America/New_York')
              .toFormat('LL-dd-yyyy hh:mm a'),
        },
        {
          key: 'testCode',
          render: (data: Order) => {
            // #KAN-18
            if (user.event) {
              return data?.event?.testCode;
            } 
              return data.orderType === OrderType.online ? '87017' : '85163';
            
          },
        },
        {
          key: 'clientId',
          render: (data: Order) => data?.clientId || "-",
        },
        { key: 'testName', render: () => 'HeartMap' },
        {
          key: 'accessionNumber',
          render: (data: Order) =>
            order?.labResult?.accession ? data?.labResult?.accession : '-',
        },
      ].filter(e => e) as Field[],
    [order?.labResult?.accession, user.event],
  );

  return (
    <div>
      <div className="flex flex-row items-center">
        <ProfileBadge
          firstName={user.firstName}
          lastName={user.lastName}
          size={64}
        />
        <div className="flex flex-col ml-[20px] flex-1">
          <span className="text-2xl font-semibold">
            {user.firstName} {user.lastName}
          </span>
          <span className="text-md text-gray-500 truncate w-[95%]">
            {user.email}
          </span>
        </div>
      </div>

      <div className="mt-[24px] flex flex-row items-center">
        <span className="material-symbols-outlined text-blue">account_box</span>
        <span className="text-base text-[#444444] ml-[17px]">
          Personal info
        </span>
      </div>

      <div className="mt-[10px]">
        <ListDetails data={user} fields={personalInfoFields} />
      </div>

      <div className="mt-[24px] flex flex-row items-center">
        <span className="material-symbols-outlined text-blue">store</span>
        <span className="text-base text-[#444444] ml-[17px]">Address info</span>
      </div>

      <div className="mt-[10px]">
        <div className="rounded border p-[12px] flex flex-col">
          <span className="text-base text-gray-500">Address</span>
          <span className="text-base mt-[8px]">{user.address}</span>
        </div>
      </div>

      <div className="mt-[24px] flex flex-row items-center">
        <span className="material-symbols-outlined text-blue">beenhere</span>
        <span className="text-base text-[#444444] ml-[17px]">Order info</span>
      </div>

      <div className="mt-[10px]">
        <ListDetails data={order} fields={orderInfoFields} />
      </div>
    </div>
  );
};

export default OrderUser;
